import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

/**
 * Colori di stato (successo / attenzione / errore / informazione), UNA sola definizione per
 * Tag, Message e Badge: «warn» e «info» devono essere lo stesso colore ovunque (prima il Tag
 * usava orange/sky e il Message yellow/blue). Regola: testo del colore pieno su fondo tenue
 * dello stesso colore, mai testo bianco su colore pieno; rapporto >= 4,5:1 in entrambi i temi
 * (misure nel commit). Chiaro: X.700 su X.50. Scuro: X.300 su X.500 al 16%.
 */
const STATI = {
  success: 'emerald',
  info: 'blue',
  warn: 'amber',
  danger: 'red',
} as const

const tonoTag = (tono: string, chiaro: boolean) =>
  chiaro
    ? { background: `{${tono}.50}`, color: `{${tono}.700}` }
    : { background: `color-mix(in srgb, {${tono}.500}, transparent 84%)`, color: `{${tono}.300}` }

const tonoMessage = (tono: string, chiaro: boolean) => {
  const t = tonoTag(tono, chiaro)
  return {
    ...t,
    borderColor: chiaro ? `{${tono}.200}` : `color-mix(in srgb, {${tono}.700}, transparent 64%)`,
    outlined: { color: t.color, borderColor: t.color },
    simple: { color: t.color },
  }
}

const stati = (chiaro: boolean) => ({
  success: tonoTag(STATI.success, chiaro),
  info: tonoTag(STATI.info, chiaro),
  warn: tonoTag(STATI.warn, chiaro),
  danger: tonoTag(STATI.danger, chiaro),
})

const messaggi = (chiaro: boolean) => ({
  success: tonoMessage(STATI.success, chiaro),
  info: tonoMessage(STATI.info, chiaro),
  warn: tonoMessage(STATI.warn, chiaro),
  error: tonoMessage(STATI.danger, chiaro),
})

/** Pulsante pieno: bianco su colore, >= 4,5:1 (Aura usa il tono 500: 2,3-4,5:1). */
const pieno = (base: string, tono: number, hover: number, active: number) => ({
  background: `{${base}.${tono}}`,
  hoverBackground: `{${base}.${hover}}`,
  activeBackground: `{${base}.${active}}`,
  borderColor: `{${base}.${tono}}`,
  hoverBorderColor: `{${base}.${hover}}`,
  activeBorderColor: `{${base}.${active}}`,
  focusRing: { color: `{${base}.${tono}}`, shadow: 'none' },
})

/**
 * Preset base di Immigreer: Aura + primario indigo + le poche correzioni che rendono i
 * componenti della libreria coerenti con il design system (base visiva presa da ImmiCare).
 *
 * <p>Si interviene QUI e non pagina per pagina: il fondo della pagina è grigio e il contenuto vive
 * su schede, quindi l'aspetto della scheda (raggio, padding) è una decisione sola che vale per
 * tutte le pagine che usano `Panel` o `Card`.
 *
 * <p>L'ombra della scheda non passa di qui: `Panel` non espone un token d'ombra, quindi vive in
 * `styles/global.css` come `--ombra-scheda`, accanto agli alias che si invertono col tema.
 *
 * <p>Il colore primario si cambia a runtime da `AppConfig` / dall'azienda (`updatePreset`): queste
 * correzioni stanno in `components` e non toccano il `semantic.primary`, quindi sopravvivono.
 */
export const ImmigReerPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
    colorScheme: {
      light: {
        // Testo smorzato: slate.500 era 4,34:1 su slate.100 (superfici tenui) → slate.600.
        text: { mutedColor: '{surface.600}', hoverMutedColor: '{surface.700}' },
        // Bordo del campo: slate.300 era 1,48:1 su bianco; i componenti UI vogliono >= 3:1.
        formField: { borderColor: '#78879d', hoverBorderColor: '{surface.600}' },
      },
      dark: {
        // slate.600 su slate.950 era 2,66:1 → slate.500 (4,24:1).
        formField: { borderColor: '{surface.500}', hoverBorderColor: '{surface.400}' },
      },
    },
  },
  components: {
    // La scheda. Raggio xl (12px): è il raggio che distingue il contenitore dal contenuto.
    panel: {
      root: {
        borderRadius: '{border.radius.xl}',
      },
      header: {
        padding: '0.75rem 1rem',
      },
      title: {
        fontWeight: '600',
      },
      content: {
        padding: '0 1rem 1rem 1rem',
      },
      footer: {
        padding: '0 1rem 1rem 1rem',
      },
    },
    card: {
      root: {
        borderRadius: '{border.radius.xl}',
      },
      body: {
        padding: '1rem',
      },
      title: {
        fontSize: '1rem',
        fontWeight: '600',
      },
    },
    // Badge e Tag di stato: testo del colore pieno su fondo tenue (stesso criterio, stessi colori).
    tag: {
      colorScheme: { light: stati(true), dark: stati(false) },
    },
    message: {
      colorScheme: { light: messaggi(true), dark: messaggi(false) },
    },
    // Contatori (Badge): restano pieni, per leggersi come segnale, ma con bianco >= 4,5:1.
    badge: {
      colorScheme: {
        light: {
          primary: { background: '{primary.600}' },
          success: { background: '{green.700}' },
          info: { background: '{sky.700}' },
          warn: { background: '{orange.700}' },
          danger: { background: '{red.600}' },
        },
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: pieno('primary', 600, 700, 800),
            success: pieno('green', 700, 800, 900),
            danger: pieno('red', 600, 700, 800),
            warn: pieno('orange', 700, 800, 900),
            info: pieno('sky', 700, 800, 900),
          },
        },
      },
    },
  },
})
